import { Link, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client';
import type { Asset, AssetState, Title } from '@dfx-transfer/shared-types';
import { RoleGuard } from '../components/RoleGuard';

interface TitleDetailResponse {
  title: Title;
  assets: Asset[];
}

export function TitleDetailPage() {
  return (
    <RoleGuard allow={['dfx_super_admin', 'org_admin', 'org_uploader']}>
      <TitleDetailInner />
    </RoleGuard>
  );
}

function TitleDetailInner() {
  const { titleId } = useParams<{ titleId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const detail = useQuery({
    queryKey: ['title', titleId],
    enabled: !!titleId,
    refetchInterval: 5000,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<TitleDetailResponse>(`/api/v1/titles/${titleId}`, { token });
    },
  });

  if (detail.isLoading) return <div>Loading…</div>;
  if (detail.isError) return <div className="text-[color:var(--color-error)]">{String(detail.error)}</div>;
  if (!detail.data) return null;

  const { title, assets } = detail.data;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/titles" className="text-sm text-[color:var(--color-text-secondary)]">
          ← Titles
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">{title.title}</h1>
        <div className="mt-1 font-mono text-xs text-[color:var(--color-text-muted)]">
          {title.external_id} · {title.content_type} · {title.state}
        </div>
      </div>

      {title.external_id && <UploadInstructions externalId={title.external_id} />}

      <section>
        <h2 className="mb-4 text-lg font-medium">Uploaded assets</h2>
        {assets.length ? (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[color:var(--color-border)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Filename</th>
                <th className="py-2 pr-4">Lang</th>
                <th className="py-2 pr-4">Size</th>
                <th className="py-2 pr-4">Uploaded</th>
                <th className="py-2 pr-4">State</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id} className="border-b border-[color:var(--color-border)]">
                  <td className="py-2 pr-4 font-mono text-xs">{a.asset_type}</td>
                  <td className="py-2 pr-4">{a.filename}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{a.language_code ?? '—'}</td>
                  <td className="py-2 pr-4 font-mono text-xs">
                    {a.size_bytes ? formatSize(a.size_bytes) : '—'}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs">
                    {a.uploaded_at ? new Date(a.uploaded_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 pr-4">
                    <AssetStatePill state={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="rounded border border-dashed border-[color:var(--color-border)] p-6 text-center text-[color:var(--color-text-secondary)]">
            No uploads yet. Connect over SFTP and upload into the title's folder.
          </div>
        )}
      </section>
    </div>
  );
}

function UploadInstructions({ externalId }: { externalId: string }) {
  return (
    <section className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-5 text-sm">
      <h2 className="mb-2 text-base font-medium">Folder layout</h2>
      <p className="text-[color:var(--color-text-secondary)]">
        Upload into the SFTP folder named after this title's external ID. Use the structure
        below — filenames in <code>metadata.csv</code> must match the uploaded files exactly
        (case-sensitive).
      </p>
      <pre className="mt-3 overflow-auto rounded bg-[color:var(--color-bg-primary)] p-3 font-mono text-xs leading-relaxed text-[color:var(--color-text-secondary)]">
{`${externalId}/
  metadata.csv
  video/${externalId}_feature.mov
  video/${externalId}_trailer.mov     (optional)
  audio/${externalId}_<lang>.wav
  subtitles/${externalId}_<lang>.srt  (optional)
  artwork/${externalId}_poster.png
  artwork/${externalId}_landscape.png
  artwork/${externalId}_hero.jpg`}
      </pre>
    </section>
  );
}

function AssetStatePill({ state }: { state: AssetState }) {
  const map: Record<AssetState, { label: string; color: string }> = {
    expected: { label: 'Expected', color: 'var(--color-text-muted)' },
    received: { label: 'Received', color: 'var(--color-info)' },
    validating: { label: 'Validating', color: 'var(--color-info)' },
    validated: { label: 'Validated', color: 'var(--color-success)' },
    failed: { label: 'Failed', color: 'var(--color-error)' },
  };
  const m = map[state];
  return (
    <span
      className="rounded-full border px-2 py-0.5 font-mono text-xs"
      style={{ color: m.color, borderColor: m.color }}
    >
      {m.label}
    </span>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
